import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { injectIntl } from "react-intl";
import Typography from "@material-ui/core/Typography";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Radio from "@material-ui/core/Radio";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import { DataGridItem } from "components/data-grid";
import { StyledTextField } from "components/form/form-textfield/form-textfield";
import withStyles from "components/with-styles";
import styles from "./styles/AdvancedSettingsSection.css.js";

const AdvancedSettingsSection = ({ intl, values, setValue, classes }) => {
  const [executionType, setExecutionType] = useState("cloud");
  const [hostNames, setHostNames] = useState([""]);

  useEffect(() => {
    if (values.file?.ami) {
      setExecutionType("cloud");
    } else if (values.file?.instance !== undefined) {
      setExecutionType("server");
      setHostNames([values.file.instance]);
    } else if (values.file?.instances) {
      setExecutionType("server");
      setHostNames(values.file.instances);
    } else {
      setExecutionType("cloud");
      setValue("file.ami", "ami_synth");
    }

    if (values.file?.logs_keeper === true && !values.file?.logs_keeper_config) {
      setValue("file.logs_keeper_config", {
        bandwidth: "10M",
        period: "60",
      });
    }
  }, []);

  const handleExecutionTypeChange = e => {
    const newType = e.target.value;
    setExecutionType(newType);

    const newFile = { ...values.file };
    delete newFile.ami;
    delete newFile.instance;
    delete newFile.instances;

    if (newType === "cloud") {
      newFile.ami = "ami_synth";
      setValue("file", newFile);
    } else {
      setHostNames([""]);
      newFile.instance = "";
      setValue("file", newFile);
    }
  };

  const handleAmiChange = e => {
    setValue("file.ami", e.target.value);
  };

  const handleHostNameChange = index => e => {
    const newHostNames = [...hostNames];
    newHostNames[index] = e.target.value;
    setHostNames(newHostNames);

    const newFile = { ...values.file };
    delete newFile.instance;
    delete newFile.instances;

    if (newHostNames.length === 1) {
      newFile.instance = newHostNames[0];
    } else {
      newFile.instances = newHostNames;
    }
    setValue("file", newFile);
  };

  const handleAddHostName = () => {
    if (hostNames.length < 2) {
      const newHostNames = [...hostNames, ""];
      setHostNames(newHostNames);

      const newFile = { ...values.file };
      delete newFile.instance;
      newFile.instances = newHostNames;
      setValue("file", newFile);
    }
  };

  const handleRemoveHostName = index => () => {
    if (hostNames.length > 1) {
      const newHostNames = hostNames.filter((_, i) => i !== index);
      setHostNames(newHostNames);

      const newFile = { ...values.file };
      delete newFile.instances;
      newFile.instance = newHostNames[0];
      setValue("file", newFile);
    }
  };

  return (
    <>
      <DataGridItem big>
        <Accordion className={classes.accordion}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="advanced-settings-content"
            id="advanced-settings-header"
          >
            <Typography variant="h6">
              {intl.formatMessage(
                { id: "roomTemplate.advancedSettings.title" },
                { defaultMessage: "Advanced Settings" },
              )}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <div className={classes.accordionContent}>
              <Typography variant="caption" color="textSecondary">
                {intl.formatMessage(
                  { id: "roomTemplate.advancedSettings.instructions" },
                  { defaultMessage: "Configure advanced settings for the room template" },
                )}
              </Typography>

              <div>
                <Typography variant="subtitle2" gutterBottom>
                  {intl.formatMessage(
                    { id: "roomTemplate.advancedSettings.execution" },
                    { defaultMessage: "Execution" },
                  )}
                </Typography>

                <div className={classes.executionContainer}>
                  {/* Cloud row */}
                  <div className={classes.executionRow}>
                    <FormControlLabel
                      value="cloud"
                      control={
                        <Radio
                          color="primary"
                          checked={executionType === "cloud"}
                          onChange={handleExecutionTypeChange}
                        />
                      }
                      label={intl.formatMessage(
                        { id: "roomTemplate.advancedSettings.cloud" },
                        { defaultMessage: "Cloud" },
                      )}
                      className={classes.radioLabel}
                    />
                    {executionType === "cloud" && (
                      <StyledTextField
                        type="text"
                        value={values.file?.ami || "ami_synth"}
                        onChange={handleAmiChange}
                        label={intl.formatMessage(
                          { id: "roomTemplate.advancedSettings.amiName" },
                          { defaultMessage: "AMI name" },
                        )}
                        className={classes.amiInput}
                      />
                    )}
                  </div>

                  {/* Server row */}
                  <div className={classes.executionRow}>
                    <FormControlLabel
                      value="server"
                      control={
                        <Radio
                          color="primary"
                          checked={executionType === "server"}
                          onChange={handleExecutionTypeChange}
                        />
                      }
                      label={intl.formatMessage(
                        { id: "roomTemplate.advancedSettings.server" },
                        { defaultMessage: "Server" },
                      )}
                      className={classes.radioLabel}
                    />
                    {executionType === "server" && (
                      <div className={classes.hostNamesContainer}>
                        {hostNames.map((hostName, index) => (
                          <div key={index} className={classes.hostNameRow}>
                            <StyledTextField
                              type="text"
                              value={hostName}
                              onChange={handleHostNameChange(index)}
                              label={intl.formatMessage(
                                { id: "roomTemplate.advancedSettings.hostName" },
                                { defaultMessage: "Host name" },
                              )}
                              className={classes.hostNameInput}
                            />
                            {hostNames.length === 2 && index === 1 && (
                              <IconButton
                                color="secondary"
                                onClick={handleRemoveHostName(index)}
                                size="small"
                                aria-label="remove host"
                              >
                                <DeleteIcon />
                              </IconButton>
                            )}
                          </div>
                        ))}
                        {hostNames.length < 2 && (
                          <Button
                            size="small"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={handleAddHostName}
                            className={classes.addHostButton}
                          >
                            {intl.formatMessage(
                              { id: "roomTemplate.advancedSettings.addHost" },
                              { defaultMessage: "Add Host" },
                            )}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <Typography variant="subtitle2" gutterBottom>
                  {intl.formatMessage(
                    { id: "roomTemplate.advancedSettings.outputSettings" },
                    { defaultMessage: "Output Settings" },
                  )}
                </Typography>
                <div className={classes.outputGrid}>
                  <StyledTextField
                    type="number"
                    value={values.file?.output_fps ?? 30}
                    onChange={e => setValue("file.output_fps", e.target.value === "" ? null : parseInt(e.target.value))}
                    label={intl.formatMessage(
                      { id: "roomTemplate.advancedSettings.outputFps" },
                      { defaultMessage: "Output FPS" },
                    )}
                  />
                  <StyledTextField
                    type="number"
                    value={values.file?.output_size ?? 1080}
                    onChange={e => setValue("file.output_size", e.target.value)}
                    label={intl.formatMessage(
                      { id: "roomTemplate.advancedSettings.outputSize" },
                      { defaultMessage: "Output Size" },
                    )}
                  />

                  {/* Second row - Boolean radio buttons */}
                  <div>
                    <Typography variant="body2" gutterBottom>
                      {intl.formatMessage(
                        { id: "roomTemplate.advancedSettings.videoProxy" },
                        { defaultMessage: "Video Proxy" },
                      )}
                    </Typography>
                    <RadioGroup
                      row
                      value={values.file?.video_proxy === true ? "yes" : "no"}
                      onChange={e => setValue("file.video_proxy", e.target.value === "yes")}
                    >
                      <FormControlLabel
                        value="yes"
                        control={<Radio color="primary" />}
                        label={intl.formatMessage({ id: "common.yes" }, { defaultMessage: "Yes" })}
                      />
                      <FormControlLabel
                        value="no"
                        control={<Radio color="primary" />}
                        label={intl.formatMessage({ id: "common.no" }, { defaultMessage: "No" })}
                      />
                    </RadioGroup>
                  </div>

                  <div>
                    <Typography variant="body2" gutterBottom>
                      {intl.formatMessage({ id: "roomTemplate.advancedSettings.mixer2" }, { defaultMessage: "Mixer2" })}
                    </Typography>
                    <RadioGroup
                      row
                      value={values.file?.mixer2 === true ? "yes" : "no"}
                      onChange={e => setValue("file.mixer2", e.target.value === "yes")}
                    >
                      <FormControlLabel
                        value="yes"
                        control={<Radio color="primary" />}
                        label={intl.formatMessage({ id: "common.yes" }, { defaultMessage: "Yes" })}
                      />
                      <FormControlLabel
                        value="no"
                        control={<Radio color="primary" />}
                        label={intl.formatMessage({ id: "common.no" }, { defaultMessage: "No" })}
                      />
                    </RadioGroup>
                  </div>

                  <div>
                    <Typography variant="body2" gutterBottom>
                      {intl.formatMessage(
                        { id: "roomTemplate.advancedSettings.fluentbit" },
                        { defaultMessage: "Fluentbit" },
                      )}
                    </Typography>
                    <RadioGroup
                      row
                      value={values.file?.fluentbit === true ? "yes" : "no"}
                      onChange={e => setValue("file.fluentbit", e.target.value === "yes")}
                    >
                      <FormControlLabel
                        value="yes"
                        control={<Radio color="primary" />}
                        label={intl.formatMessage({ id: "common.yes" }, { defaultMessage: "Yes" })}
                      />
                      <FormControlLabel
                        value="no"
                        control={<Radio color="primary" />}
                        label={intl.formatMessage({ id: "common.no" }, { defaultMessage: "No" })}
                      />
                    </RadioGroup>
                  </div>

                  {/* Third row - Logs Keeper */}
                  <div>
                    <Typography variant="body2" gutterBottom>
                      {intl.formatMessage(
                        { id: "roomTemplate.advancedSettings.logsKeeper" },
                        { defaultMessage: "Logs Keeper" },
                      )}
                    </Typography>
                    <RadioGroup
                      row
                      value={values.file?.logs_keeper === true ? "yes" : "no"}
                      onChange={e => {
                        const isYes = e.target.value === "yes";
                        setValue("file.logs_keeper", isYes);
                        if (isYes && !values.file?.logs_keeper_config) {
                          setValue("file.logs_keeper_config", {
                            bandwidth: "10M",
                            period: "60",
                          });
                        } else if (!isYes) {
                          const newFile = { ...values.file };
                          delete newFile.logs_keeper_config;
                          setValue("file", newFile);
                        }
                      }}
                    >
                      <FormControlLabel
                        value="yes"
                        control={<Radio color="primary" />}
                        label={intl.formatMessage({ id: "common.yes" }, { defaultMessage: "Yes" })}
                      />
                      <FormControlLabel
                        value="no"
                        control={<Radio color="primary" />}
                        label={intl.formatMessage({ id: "common.no" }, { defaultMessage: "No" })}
                      />
                    </RadioGroup>
                  </div>
                </div>

                {/* Logs Keeper Config - Conditional inputs */}
                {values.file?.logs_keeper === true && (
                  <div className={classes.logsKeeperConfig}>
                    <Typography variant="subtitle2" gutterBottom>
                      {intl.formatMessage(
                        { id: "roomTemplate.advancedSettings.logsKeeperConfig" },
                        { defaultMessage: "Logs Keeper Configuration" },
                      )}
                    </Typography>
                    <div className={classes.logsKeeperInputs}>
                      <StyledTextField
                        type="text"
                        value={values.file?.logs_keeper_config?.bandwidth ?? "10M"}
                        onChange={e => setValue("file.logs_keeper_config.bandwidth", e.target.value)}
                        label={intl.formatMessage(
                          { id: "roomTemplate.advancedSettings.bandwidth" },
                          { defaultMessage: "Bandwidth" },
                        )}
                        className={classes.logsKeeperInput}
                      />
                      <StyledTextField
                        type="text"
                        value={values.file?.logs_keeper_config?.period ?? "60"}
                        onChange={e => setValue("file.logs_keeper_config.period", e.target.value)}
                        label={intl.formatMessage(
                          { id: "roomTemplate.advancedSettings.period" },
                          { defaultMessage: "Period" },
                        )}
                        className={classes.logsKeeperInput}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </AccordionDetails>
        </Accordion>
      </DataGridItem>
    </>
  );
};

AdvancedSettingsSection.propTypes = {
  intl: PropTypes.object.isRequired,
  values: PropTypes.object.isRequired,
  setValue: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
};

export default injectIntl(withStyles(styles)(AdvancedSettingsSection));
